// Client-side mirror of the server's IdNameRules
// (configuration/src/core/Retail.ConfigServer.Core/Naming/IdNameRules.cs).
//
// Canonical identifier rule for every operator-entered id — group, project, service,
// environment, API-key id, and each segment of a secret reference:
//
//   ^[a-z][a-z0-9]*(-[a-z0-9]+)*$   (lowercase, alpha-leading, single hyphens
//                                    between alnum runs, no leading/trailing/'--')
//   length <= 64
//
// allowUppercase=true is the same shape but case-PRESERVING (A–Z permitted). Only secret
// scope/name use it — folder-backed entity names stay lowercase for filesystem/Key Vault
// consistency.
//
// The SPA normalises input LIVE so the operator sees the stored form as they type
// ("Foo Bar" -> "foo-bar"); the server independently rejects anything off-spec so
// non-SPA callers can't introduce inconsistency.

export const ID_NAME_MAX = 64;

export const ID_NAME_REGEX = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;
export const ID_NAME_MIXED_REGEX = /^[A-Za-z][A-Za-z0-9]*(-[A-Za-z0-9]+)*$/;

export const ID_NAME_DESCRIPTION =
  'must be lowercase, start with a letter, and contain only letters, digits, and '
  + "single hyphens (no leading/trailing hyphen, no '--')";

export const ID_NAME_MIXED_DESCRIPTION =
  'must start with a letter and contain only letters, digits, and single hyphens '
  + "(no leading/trailing hyphen, no '--')";

// Live, as-you-type transform. Deliberately keeps a single trailing '-' so a user
// mid-word after a space still works ("a b": "a" -> "a-" -> "a-b"); stripping it live
// would eat the separator. The field reads invalid for that one keystroke (submit
// gated) and the trailing '-' is removed on submit via { trimTrailingDash: true }.
export function normalizeIdName(
  raw: string,
  opts: { trimTrailingDash?: boolean; allowUppercase?: boolean } = {},
): string {
  const upper = opts.allowUppercase === true;
  let v = (raw ?? '');
  if (!upper) v = v.toLowerCase();
  v = v
    .replace(/\s+/g, '-')                                       // whitespace runs -> single dash
    .replace(upper ? /[^A-Za-z0-9-]/g : /[^a-z0-9-]/g, '')      // drop off-charset chars
    .replace(/-{2,}/g, '-')                                     // collapse '--' runs
    .replace(upper ? /^[^A-Za-z]+/ : /^[^a-z]+/, '');           // must start with a letter
  if (opts.trimTrailingDash) {
    v = v.replace(/-+$/, '');
  }
  return v.slice(0, ID_NAME_MAX);
}

// Returns a human error string when `value` is non-empty and invalid, else null.
// Empty is treated as "not an error here" so the caller's submit gate / required-field
// handling owns that case (matches the pre-existing nameFormatProblem contract).
export function idNameProblem(
  value: string,
  opts: { reserved?: ReadonlySet<string>; allowUppercase?: boolean } = {},
): string | null {
  const v = value.trim();
  if (v === '') return null;
  if (v.length > ID_NAME_MAX) return `Name must be ${ID_NAME_MAX} characters or fewer.`;
  if (opts.reserved?.has(v.toLowerCase())) return `'${v}' is reserved.`;
  const upper = opts.allowUppercase === true;
  const re = upper ? ID_NAME_MIXED_REGEX : ID_NAME_REGEX;
  if (!re.test(v)) return `Name ${upper ? ID_NAME_MIXED_DESCRIPTION : ID_NAME_DESCRIPTION}.`;
  return null;
}
